// URL-контракт воркспейса курса — ОДНА Zod-схема на ОБА роута (декрет E11).
//
// Воркспейс рендерят два независимых роута (`/courses/$courseId` и
// `/courses/$courseId/topics/$topicId`, декрет E9 — `<Outlet/>` не вводим).
// Схема живёт здесь, а не в файлах роутов: две копии разъезжаются, и
// deep-link, собранный для одного роута, молча теряет параметры на другом.
//
// Что НЕЛЬЗЯ удалять:
//   • `demo` — контракт ревью состояний, есть у всех четырёх роутов курса;
//   • `read` — легаси-ссылки на чтение конспекта (`?read=note`). Помечен
//     @deprecated и приводится к `view=note` функцией `normalizeSearch`.
import { TOPIC_RECAP_FORMATS } from "@studyhub/shared/learning";
import { z } from "zod";

/**
 * Виды центральной сцены. Порядок значений — порядок прохождения темы:
 * ① разобраться (`note` / `audio` / `note-editor`) → ② проверить себя
 * (`cards` / `quiz`) → ③ добрать (`gap`) → `done`. `calendar`, `tools` и
 * `builder` — поперечные виды, в маршрут ①②③ не входят.
 */
export const COURSE_WORKSPACE_VIEWS = [
	// Разводящие шагов ①② — клик по этапу ведёт сюда, не сразу в материал.
	"step1",
	"step2",
	"note",
	"audio",
	"cards",
	"quiz",
	// Шаг ③ «Добрать» сам является разводящей (`SceneGap`).
	"gap",
	"done",
	"note-editor",
	// Голосовое занятие идёт ВНУТРИ воркспейса: панель курса слева и репетитор
	// справа остаются на месте. Раньше плитка уводила на overlay-роут
	// `/voice-tutor/session/$id`, и человек выпадал из курса на весь экран.
	"voice",
	// Чат с персонажем — тот же контракт, что у голоса: без ухода на
	// `/characters/sessions/$id` из шага «Добрать».
	"character",
	"calendar",
	"tools",
	// Конструктор курса (§5.2): единственный вид, доступный и БЕЗ программы —
	// им её как раз и собирают вручную.
	"builder",
	// Этап настройки курса — нулевой узел маршрута ДО «Модуль 1». Не тема:
	// диагностика не занимает `view=quiz` и не подменяет referenceId темы.
	"placement",
] as const;
export type CourseWorkspaceView = (typeof COURSE_WORKSPACE_VIEWS)[number];

/** Состояния для ревью (`?demo=`) — общий контракт всех роутов курса. */
export const COURSE_WORKSPACE_DEMOS = ["default", "loading", "empty", "error"] as const;
export type CourseWorkspaceDemo = (typeof COURSE_WORKSPACE_DEMOS)[number];

export const courseWorkspaceSearchSchema = z.object({
	/** Активный вид сцены. Не задан — каркас выбирает по спине темы. */
	view: z.enum(COURSE_WORKSPACE_VIEWS).optional(),
	/** Формат аудио-канала темы (`summary` / `lecture` / `podcast`). */
	fmt: z.enum(TOPIC_RECAP_FORMATS).optional(),
	/** Конкретная заметка внутри сцены конспекта (своя заметка пользователя). */
	note: z.string().uuid().optional(),
	/**
	 * Конкретный тест темы (базовый или аддитивный). Без параметра — первичный
	 * `readActivity(..., "quiz")`.
	 */
	quiz: z.string().uuid().optional(),
	/**
	 * Конкретный набор карточек темы. Без параметра — первичный flashcards.
	 */
	cards: z.string().uuid().optional(),
	/**
	 * Откуда открыли сцену артефакта. «Назад» в шапке темы ведёт на этот шаг,
	 * а не на ①/②: тест/карточки/заметка со шага ③ должны вернуть на «Добрать».
	 */
	from: z.enum(["gap"]).optional(),
	/** Режим правки конспекта. Число, а не строка: TanStack Router парсит JSON. */
	edit: z.literal(1).optional(),
	/**
	 * Сданная попытка теста темы. Держим в URL, а не в стейте сцены: подвал
	 * каркаса обязан узнать о сдаче СРАЗУ, не дожидаясь, пока сервер закроет
	 * активность и догонит PowerSync — иначе после отправки теста человек
	 * упирался в подпись «Ответьте на вопросы, чтобы продолжить» без единой
	 * кнопки вперёд (жалоба 2026-08-09). Плюс разбор переживает перезагрузку.
	 */
	attempt: z.string().uuid().optional(),
	/**
	 * Идущее голосовое занятие (`voice_sessions.id`). Держим в URL, а не в стейте
	 * сцены: занятие переживает перезагрузку и уход на другой вид (сервер
	 * оживляет сессию через `sessions.resume`), а ссылкой на него можно
	 * поделиться — это канон воркспейса (декрет E11, состояние в URL).
	 */
	voice: z.string().uuid().optional(),
	/**
	 * Идущий чат с персонажем (`character_sessions.id`). В URL, не в стейте:
	 * переживает перезагрузку и возврат с другого вида (декрет E11).
	 */
	character: z.string().uuid().optional(),
	/** Демо-состояние для ревью. */
	demo: z.enum(COURSE_WORKSPACE_DEMOS).optional(),
	/**
	 * @deprecated Легаси-ссылка на чтение конспекта. Читается через
	 * `normalizeCourseWorkspaceSearch` как `view=note`; новые ссылки строим с
	 * `view`. Не удалять — параметр разослан в письмах и уведомлениях.
	 */
	read: z.enum(["note"]).optional(),
});

export type CourseWorkspaceSearch = z.infer<typeof courseWorkspaceSearchSchema>;

/**
 * Приведение легаси-параметров к канону. Единственное место, где читается
 * `read` — все потребители смотрят только на `view`.
 */
export function normalizeCourseWorkspaceSearch(
	search: CourseWorkspaceSearch,
): CourseWorkspaceSearch {
	if (search.view || !search.read) return search;
	return { ...search, view: "note" };
}

/**
 * Идёт ПРАВКА конспекта.
 *
 * Признак живёт в двух ключах сразу (`edit=1` и легаси-вид `note-editor`),
 * поэтому читаем его одной функцией: сцена конспекта решает по нему, что
 * показывать («Готово» вместо «Редактировать», рельс форматирования), а каркас —
 * убирать ли нижний хром на мобильном (подвал шага + полоса вызова: они
 * поднимаются вместе с экранной клавиатурой и съедают всю область правки).
 * Две копии условия разъехались бы на первой же правке одного из них.
 */
export function isNoteEditingSearch(search: CourseWorkspaceSearch): boolean {
	return search.edit === 1 || search.view === "note-editor";
}

/** Виды, которые нельзя открыть без активной темы (курс без программы). */
const TOPIC_ONLY_VIEWS: ReadonlySet<CourseWorkspaceView> = new Set([
	"step1",
	"step2",
	"note",
	"audio",
	"cards",
	"quiz",
	"gap",
	"done",
	"note-editor",
	// Занятие строится по плану урока ТЕМЫ — без темы открывать нечего.
	"voice",
	"character",
]);

export function viewRequiresTopic(view: CourseWorkspaceView): boolean {
	return TOPIC_ONLY_VIEWS.has(view);
}
